/**
 * Represents a single option of a single filter.
 *
 * For instance, if the filter `Color` had options `Red`, `Yellow`, `Green`, each of these would be represented by a
 * `FoundVacanciesOption`.
 */
export class FoundVacanciesOption {
	// Number of docs matching this filter (?)
	docs: number;
	label: string;
	// Number of docs left if this filter is enabled (?)
	remainingCount: number;
	value: string;
	selected: boolean;
	disabled: boolean;

	constructor(serverResponse: any) {
		this.docs = serverResponse.docs || 0;
		this.label = serverResponse.label || "";
		this.remainingCount = serverResponse.remainingCount || 0;
		this.value = serverResponse.value || "";
		this.selected = serverResponse.selected || false;
		this.disabled = serverResponse.disabled || false;
	}

	toJS(): {[key in keyof FoundVacanciesOption]?: any} {
		return {
			docs: this.docs,
			label: this.label,
			remainingCount: this.remainingCount,
			value: this.value,
			selected: this.selected,
			disabled: this.disabled,
		}
	}
}

/**
 * Represents a single vacancy Filter, including all its options.
 *
 * For instance, the filter `Color` with options `Red`, `Green`, `Blue` would be represented by a
 * `FoundVacanciesFilter`.
 */
export class FoundVacanciesFilter {
	enabled: boolean;
	name: string;
	options: FoundVacanciesOption[];
	type: string;
	usable: boolean;

	constructor(serverResponse: any) {
		if (serverResponse.name == null) {
			console.error("No name for filter", serverResponse);
			throw new Error("No name for filter");
		}

		if (serverResponse.type == null) {
			console.error("No type for filter", serverResponse);
			throw new Error("No type for filter");
		}

		this.enabled = serverResponse.enabled || false;
		this.name = serverResponse.name;
		this.type = serverResponse.type;
		this.usable = serverResponse.usable || false;
		this.options = this.convertServerResponseToOptions(serverResponse);
	}

	toJS(): {[key in keyof FoundVacanciesFilter]?: any} {
		return {
			enabled: this.enabled,
			name: this.name,
			options: this.options.map(option => option.toJS()),
			type: this.type,
			usable: this.usable
		}
	}

	private convertServerResponseToOptions(serverResponse: any): FoundVacanciesOption[] {
		if(serverResponse.options == null) return [];
		return serverResponse.options.map((option: any) => new FoundVacanciesOption(option))
			.sort((optionA: FoundVacanciesOption, optionB: FoundVacanciesOption) => {
				if (optionA.selected && optionB.selected) return 0;
				if (!optionA.selected && optionB.selected) return 1;
				if (optionA.selected && !optionB.selected) return -1;

				if (optionA.disabled && optionB.disabled) return 0;
				if (!optionA.disabled && optionB.disabled) return -1;
				if (optionA.disabled && !optionB.disabled) return 1;

				if (optionA.remainingCount > 0 && optionB.remainingCount > 0) return 0;
				if (optionA.remainingCount > 0 && optionB.remainingCount <= 0) return -1;
				if (optionA.remainingCount <= 0 && optionB.remainingCount > 0) return 1;
				return 0;
			})
	}
}

/**
 * A mapping of filter names to respective `FoundVacanciesFilter` objects.
 */
export type FoundVacanciesFilterCollection = { [filterName: string]: FoundVacanciesFilter };

/**
 * Representation of the whole vacancy retrieval response, including filters and a total of matched vacancies.
 */
export class FoundVacancies {
	filters: FoundVacanciesFilterCollection;

	// Total matches for this filtering
	total: number;

	constructor(serverResponse: any) {
		// Convert all the filters into a FoundVacanciesFilterCollection
		this.filters = this.convertServerResponseToFilters(serverResponse);
		this.total = serverResponse.total;
	}

	/**
	 * Extract the filters in this response
	 * @param serverResponse root response
	 * @private
	 */
	private convertServerResponseToFilters(serverResponse: any): FoundVacanciesFilterCollection {
		return Object.keys(serverResponse.filters)
			.reduce((aggregation: FoundVacanciesFilterCollection, filterName: string) => {
				const filter = new FoundVacanciesFilter(serverResponse.filters[filterName]);
				aggregation[filter.name] = filter;
				return aggregation;
			}, {}) || {};
	}
}